import React, { useContext, useEffect, useMemo } from "react";
import { FieldArray, useFormikContext } from "formik";
import { Field, FormArray, FormGroup, GenericComponent, PInput } from "../../models/models";
import { FormContext } from "../../context/formContext";
import FieldComponent from "./FieldComponent";
import FormGroupCompnent from "./FormGroupComponent";
import { Button } from "primereact/button";
import { FormArrayConfig } from "../../../../shared/enums/FormArrayConfig";
interface FieldArrayProps {
  parentKey: string;
  elementKey: string;
  rest: any;
  label?: string;
  childModel?: any;
  parentvalues: any;
  hideExpression?: (formContext: any, values: any, model: any) => boolean;
  fields: (Field | FormGroup | FormArray | PInput | GenericComponent)[];
  mutateValues?: (formContext: any, parentvalues: any, childModel: any) => any;
  isRequired?: boolean;
  config: FormArrayConfig;
}
export default function FormArrayComponent({
  parentKey,
  elementKey,
  rest,
  parentvalues,
  hideExpression,
  label,
  childModel,
  fields,
  mutateValues,
  config,
}: FieldArrayProps) {
  const context = useContext(FormContext);
  const { values, setFieldValue } = useFormikContext();
  const { removeLabel, addLabel, isRequired } = rest ?? {};

  function setValue(value: any) {
    setFieldValue(elementKey, value);
  }

  useEffect(() => {
    let initialValues = parentvalues[elementKey] ? parentvalues[elementKey].map((x: any, i: number) => ({ key: i, ...childModel, ...x })) : null;

    if (config === FormArrayConfig.AutoAddField && initialValues && mutateValues) {
      initialValues = mutateValues?.(context.formContextStore, initialValues, childModel);
      setValue(initialValues);
    }
  }, [context.formContextStore.loaded]);

  const nextParentKey = useMemo(() => {
    return [parentKey, elementKey].filter((x) => x !== "" && x != null).join(".");
  }, [parentKey, elementKey]);

  const items = useMemo(() => {
    const value = parentvalues[elementKey];
    return value;
  }, [elementKey, parentvalues]);

  useEffect(() => {
    if (hideExpression != null) {
      const isHidden = hideExpression(context.formContextStore, values, parentvalues);
      if (isHidden !== parentvalues[elementKey + "Hidden"]) {
        setFieldValue(nextParentKey + "Hidden", isHidden);
      }
    }
  }, [hideExpression, values, parentvalues, context.formContextStore]);

  const hidden = useMemo(() => parentvalues[elementKey + "Hidden"], [parentvalues[elementKey + "Hidden"]]);

  return (
    <>
      {!hidden && (
        <FieldArray name={nextParentKey} key={"fieldarray_" + elementKey}>
          {({ remove, push }) => (
            <div key={nextParentKey}>
              {label && <label className={isRequired ? "isRequired" : ""}>{label}</label>}
              {items?.length > 0 &&
                items.map((item: any, index: any) => (
                  <div key={index} className="flex flex-column">
                    {fields.map((f, idx) => {
                      const autoProps = config === FormArrayConfig.AutoAddField && index != 0 ? { disabled: true } : {};
                      switch (f.type) {
                        case "formGroup":
                          f = f as FormGroup;
                          return (
                            <FormGroupCompnent
                              key={f.key + f.type + idx + "_" + index}
                              parentKey={nextParentKey + "." + index}
                              parentvalues={items[index]}
                              customWrapper={f.customWrapper}
                              elementKey={f.key}
                              label={f.label}
                              fields={f.fields}
                              hideExpression={f.hideExpression}
                              rest={f.props}
                            />
                          );
                        case "formArray":
                          f = f as FormArray;
                          return (
                            <div key={f.key + f.type + index}>
                              <FormArrayComponent
                                key={f.key + f.type + idx + "_" + index}
                                parentKey={nextParentKey + "." + index}
                                parentvalues={items[index]}
                                rest={f.props}
                                elementKey={f.key}
                                label={f.label}
                                fields={f.fields}
                                hideExpression={f.hideExpression}
                                mutateValues={f.mutateValues}
                                config={config}
                              />
                            </div>
                          );
                        case "field":
                          f = f as Field;
                          return (
                            <div key={f.key + f.type + index}>
                              <FieldComponent
                                key={f.key + f.type + idx + "_" + index}
                                parentKey={nextParentKey + "." + index}
                                parentvalues={items[index]}
                                elementKey={f.key}
                                fieldType={f.fieldType}
                                label={f.label}
                                warn={f.warn}
                                rest={{ ...f.props, index, ...autoProps }}
                                expressionProps={f.expressionProps}
                                hideExpression={f.hideExpression}
                                calculatedValue={f.calculatedValue}
                                extendedlabel={f.extendedlabel}
                              />
                              <div className={f.props?.advisorytextcss}>{f.props?.advisorytext}</div>
                            </div>
                          );

                        case "pInput":
                          f = f as PInput;
                          return (
                            <div key={f.key + f.type + idx + "_" + index} className={f.props?.css}>
                              {f.label}
                            </div>
                          );
                          break;
                        case "component": {
                          f = f as GenericComponent;
                          const Body = f.body;
                          return (
                            <div key={f.key + f.type + idx + "_" + index}>
                              <Body values={values} parentvalues={items[index]} />{" "}
                            </div>
                          );
                        }
                      }
                    })}
                    {config === FormArrayConfig.DeleteCustom && !context.formContextStore.disabled ? (
                      <Button
                        type="button"
                        onClick={() => {
                          if (items.length > 1) remove(index);
                        }}
                        label={removeLabel ?? "Remove"}
                        className="delete-button"
                      />
                    ) : (
                      ""
                    )}
                  </div>
                ))}

              {config === FormArrayConfig.DeleteLast && !context.formContextStore.disabled ? (
                <div>
                  <Button
                    type="button"
                    onClick={() => {
                      if (items.length > 1) remove(0);
                    }}
                    label={removeLabel ?? "Remove"}
                    className="delete-button"
                  />
                </div>
              ) : (
                ""
              )}
              {config !== FormArrayConfig.AutoAddField && !context.formContextStore.disabled ? (
                <Button type="button" onClick={() => push(childModel)} className="add-button" label={addLabel ?? "Add"} />
              ) : (
                ""
              )}
            </div>
          )}
        </FieldArray>
      )}
    </>
  );
}
